import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, ChevronLeft, ChevronRight, Clock, Users } from 'lucide-react';
import { formatMontant, formatDate, CATEGORIES_TONES } from './etatSalairesUtils';

// Pastilles de statut de paiement -- meme forme que celles de MaCaisse.
const formatMissingPointageDate = (value) => {
  if (!value) return '';
  const date = new Date(String(value) + 'T12:00:00');
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' });
};

const POINTAGES_PAGE_SIZE = 6;
const TABLE_PAGE_SIZE = 8;

const TablePagination = ({ pageIndex, pageCount, totalRows, firstIndex, lastIndex, onPrevious, onNext }) => {
  if (pageCount <= 1) return null;

  return (
    <div className="mt-4 flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-xs font-medium text-gray-500">
        Lignes {firstIndex + 1}-{lastIndex} / {totalRows}
      </p>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          title="Page précédente"
          onClick={onPrevious}
          disabled={pageIndex === 0}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft size={15} />
        </button>
        <span className="inline-flex h-8 min-w-[76px] items-center justify-center rounded-md border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700">
          Page {pageIndex + 1}/{pageCount}
        </span>
        <button
          type="button"
          title="Page suivante"
          onClick={onNext}
          disabled={pageIndex >= pageCount - 1}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronRight size={15} />
        </button>
      </div>
    </div>
  );
};
const formatMissingSlotTime = (slot = {}) => {
  const debut = String(slot.heure_debut || slot.heure_debut_prevue || '').slice(0, 5);
  const fin = String(slot.heure_fin || slot.heure_fin_prevue || '').slice(0, 5);
  return debut && fin ? debut + '-' + fin : '';
};

const PointagesManquantsCell = ({ ligne }) => {
  const [pageIndex, setPageIndex] = useState(0);
  const dates = Array.isArray(ligne?.pointages?.dates_manquantes) ? ligne.pointages.dates_manquantes : [];
  const creneaux = Array.isArray(ligne?.pointages?.creneaux_manquants_detail) ? ligne.pointages.creneaux_manquants_detail : [];
  const isHourly = String(ligne?.mode_remuneration || '').toUpperCase() === 'HORAIRE';
  const hasCreneauxDetail = isHourly && creneaux.length > 0;
  const items = hasCreneauxDetail ? creneaux : dates;
  const joursManquants = Number(
    ligne?.pointages?.jours_manquants
    || ligne?.pointages?.creneaux_manquants
    || ligne?.jours_manquants
    || items.length
    || 0
  );
  const pageCount = Math.max(1, Math.ceil(items.length / POINTAGES_PAGE_SIZE));
  const safePageIndex = Math.min(pageIndex, pageCount - 1);
  const firstIndex = safePageIndex * POINTAGES_PAGE_SIZE;
  const visibleItems = items.slice(firstIndex, firstIndex + POINTAGES_PAGE_SIZE);

  if (!joursManquants && !items.length) {
    if (isHourly) {
      return (
        <span className="inline-flex h-7 min-w-[158px] items-center justify-center rounded-full bg-blue-100 px-3 text-xs font-semibold text-blue-800 whitespace-nowrap">
          Aucun créneau détecté
        </span>
      );
    }

    return (
      <span className="inline-flex h-7 min-w-[112px] items-center justify-center rounded-full bg-green-100 px-3 text-xs font-semibold text-green-800 whitespace-nowrap">
        Complet
      </span>
    );
  }

  return (
    <div className="min-w-[220px] max-w-[320px]">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-amber-900">
          {joursManquants || items.length} {hasCreneauxDetail ? 'créneau(x)' : 'jour(s)'} manquant(s)
        </p>
        {items.length > POINTAGES_PAGE_SIZE && (
          <span className="whitespace-nowrap text-[11px] font-medium text-slate-500">
            {firstIndex + 1}-{Math.min(firstIndex + POINTAGES_PAGE_SIZE, items.length)} / {items.length}
          </span>
        )}
      </div>

      {items.length > 0 ? (
        <>
          <div className="flex flex-wrap gap-1.5">
            {visibleItems.map((item) => {
              if (!hasCreneauxDetail) {
                return (
                  <span key={item} className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-900">
                    {formatMissingPointageDate(item)}
                  </span>
                );
              }

              const key = item.libelle || [item.date_pointage, item.heure_debut, item.heure_fin, item.classe_nom, item.matiere_nom].filter(Boolean).join('-');
              const dateLabel = formatMissingPointageDate(item.date_pointage);
              const timeLabel = formatMissingSlotTime(item);
              const detailLabel = [item.matiere_nom, item.classe_nom].filter(Boolean).join(' - ');

              return (
                <span key={key} title={item.libelle || key} className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-900">
                  <span className="block whitespace-nowrap">{dateLabel}{timeLabel ? ' - ' + timeLabel : ''}</span>
                  {detailLabel && <span className="block max-w-[130px] truncate text-[10px] font-normal text-amber-700">{detailLabel}</span>}
                </span>
              );
            })}
          </div>

          {items.length > POINTAGES_PAGE_SIZE && (
            <div className="mt-2 flex items-center justify-end gap-1">
              <button
                type="button"
                title="Éléments précédents"
                onClick={(event) => {
                  event.stopPropagation();
                  setPageIndex((current) => Math.max(0, current - 1));
                }}
                disabled={safePageIndex === 0}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-amber-200 bg-white text-amber-800 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                type="button"
                title="Éléments suivants"
                onClick={(event) => {
                  event.stopPropagation();
                  setPageIndex((current) => Math.min(pageCount - 1, current + 1));
                }}
                disabled={safePageIndex >= pageCount - 1}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-amber-200 bg-white text-amber-800 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="text-xs leading-5 text-amber-800">
          Aucun détail disponible pour cette ligne.
        </p>
      )}
    </div>
  );
};

const PaymentBadge = ({ ligne }) => {
  const badgeClass = 'inline-flex h-9 min-w-[104px] items-center justify-center gap-1 rounded-full px-3 text-xs font-semibold whitespace-nowrap';

  if (ligne?.est_synthese_hybride) {
    return <span className={`${badgeClass} bg-cyan-100 text-cyan-800`}>Synthèse</span>;
  }

  if (ligne?.paiement?.existe) {
    return (
      <span className={`${badgeClass} bg-green-100 text-green-800`}>
        <CheckCircle size={13} className="shrink-0" />
        Payé
      </span>
    );
  }

  if (ligne?.paiement?.paiement_circuit_existe && Number(ligne?.deja_paye || 0) > 0) {
    return (
      <span className={`${badgeClass} bg-blue-100 text-blue-800`}>
        <Clock size={13} className="shrink-0" />
        Partiel
      </span>
    );
  }

  return <span className={`${badgeClass} bg-amber-100 text-amber-800`}>À doter</span>;
};

// Sous-section calquee sur les sous-sections de MaCaisse :
// border-2 + en-tete pastel + tableau a en-tete colore.
const SalaryTableSection = ({ title, description, data, tone = 'personnel', icon: Icon = Users }) => {
  const lignes = data?.lignes || [];
  const resume = data?.resume || {};
  const t = CATEGORIES_TONES[tone] || CATEGORIES_TONES.personnel;
  const [pageIndex, setPageIndex] = useState(0);
  const totalRows = lignes.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / TABLE_PAGE_SIZE));
  const safePageIndex = Math.min(pageIndex, pageCount - 1);
  const firstIndex = safePageIndex * TABLE_PAGE_SIZE;
  const lastIndex = Math.min(firstIndex + TABLE_PAGE_SIZE, totalRows);
  const visibleLignes = lignes.slice(firstIndex, lastIndex);

  useEffect(() => {
    setPageIndex(0);
  }, [title, totalRows]);

  useEffect(() => {
    if (pageIndex > pageCount - 1) setPageIndex(Math.max(0, pageCount - 1));
  }, [pageIndex, pageCount]);

  return (
    <div className={`border-2 ${t.border} rounded-lg ${t.bg} mb-6`}>
      <div className={`p-4 ${t.header} border-b`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className={`text-lg font-semibold ${t.title} flex items-center gap-2`}>
              <Icon size={20} />
              {title}
            </h3>
            <p className={`text-sm ${t.sub} mt-1`}>{description}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white text-gray-700">
              {resume.effectif_unique || resume.effectif || 0} employé(s)
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
              Net {formatMontant(resume.reste_a_doter)}
            </span>
            {pageCount > 1 && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white text-gray-700">
                {firstIndex + 1}-{lastIndex} / {totalRows}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="p-4">
        {lignes.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-lg border-2 border-dashed border-gray-200">
            <Icon size={40} className="mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 font-medium">Aucun employé dans cette catégorie</p>
            <p className="text-gray-500 text-sm mt-1">Aucune ligne de paie pour la période sélectionnée</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-gray-100 bg-white">
            <table className="w-full min-w-[1320px] table-fixed">
              <colgroup>
                <col className="w-[260px]" />
                <col className="w-[120px]" />
                <col className="w-[150px]" />
                <col className="w-[120px]" />
                <col className="w-[140px]" />
                <col className="w-[130px]" />
                <col className="w-[330px]" />
                <col className="w-[300px]" />
              </colgroup>
              <thead className={`${t.th} border-b`}>
                <tr>
                  <th className="px-4 py-3 text-left align-top text-xs font-semibold uppercase tracking-wide">Employé</th>
                  <th className="px-4 py-3 text-right align-top text-xs font-semibold uppercase tracking-wide">Brut</th>
                  <th className="px-4 py-3 text-right align-top text-xs font-semibold uppercase tracking-wide">Retenues</th>
                  <th className="px-4 py-3 text-right align-top text-xs font-semibold uppercase tracking-wide">Avances</th>
                  <th className="px-4 py-3 text-right align-top text-xs font-semibold uppercase tracking-wide">Net à payer</th>
                  <th className="px-4 py-3 text-center align-top text-xs font-semibold uppercase tracking-wide">Statut</th>
                  <th className="px-4 py-3 text-left align-top text-xs font-semibold uppercase tracking-wide">Pointages manquants</th>
                  <th className="px-4 py-3 text-left align-top text-xs font-semibold uppercase tracking-wide">Lecture</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${t.divide}`}>
                {visibleLignes.map((ligne, index) => (
                  <tr
                    key={ligne.ligne_key || [ligne.type_personnel, ligne.employe_id, ligne.employe_role_id || ligne.mode_remuneration || firstIndex + index].join('-')}
                    className={`${t.row} align-top transition-colors`}
                  >
                    <td className="px-4 py-4 align-top">
                      <p className="text-sm font-medium text-gray-900">{ligne.nom_complet}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">{ligne.role}</span>
                        {ligne.est_hybride && !ligne.est_synthese_hybride && (
                          <span className="inline-flex h-6 items-center whitespace-nowrap rounded bg-cyan-100 px-2 text-xs font-medium text-cyan-800">
                            Hybride · composante {ligne.mode_remuneration === 'HORAIRE' ? 'horaire' : 'mensuelle'}
                          </span>
                        )}
                        {ligne.est_synthese_hybride && (
                          <span className="inline-flex h-6 items-center whitespace-nowrap rounded bg-cyan-100 px-2 text-xs font-medium text-cyan-800">
                            {ligne.composantes_count || 0} composante(s) consolidée(s)
                          </span>
                        )}
                        {ligne.mode_remuneration === 'HORAIRE' && (
                          <span className="inline-flex h-6 items-center whitespace-nowrap rounded bg-blue-100 px-2 text-xs font-medium text-blue-800">
                            {ligne.pointages?.total_heures || 0} h calculée(s)
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-4 align-top text-right">
                      <p className="whitespace-nowrap text-sm font-semibold text-gray-900">{formatMontant(ligne.salaire_brut)}</p>
                    </td>

                    <td className="px-4 py-4 align-top text-right">
                      <p className="whitespace-nowrap text-sm font-semibold text-red-600">{formatMontant(ligne.retenues_total)}</p>
                      {Number(ligne.retenues_total || 0) > 0 && (
                        <div className="rounded-md bg-red-50 border border-red-100 px-2 py-1 text-[11px] text-red-700 space-y-0.5 mt-1">
                          <div className="flex justify-between gap-2">
                            <span>Présence</span>
                            <span className="font-semibold">{formatMontant(ligne.retenues_presence)}</span>
                          </div>
                          <div className="flex justify-between gap-2">
                            <span>Enfants</span>
                            <span className="font-semibold">{formatMontant(ligne.retenues_enfants)}</span>
                          </div>
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-4 align-top text-right">
                      <p className="whitespace-nowrap text-sm font-semibold text-amber-600">{formatMontant(ligne.avances_total)}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{ligne.avances_nombre || 0} avance(s)</p>
                    </td>

                    <td className="px-4 py-4 align-top text-right">
                      <p className="text-base font-bold text-[#246990]">{formatMontant(ligne.net_a_payer)}</p>
                      {ligne.deja_paye > 0 && (
                        <p className="text-xs text-green-600 mt-0.5">Déjà payé {formatMontant(ligne.deja_paye)}</p>
                      )}
                    </td>

                    <td className="px-4 py-4 align-top text-center">
                      <div className="flex min-w-[104px] flex-col items-center gap-1">
                        <PaymentBadge ligne={ligne} />
                        {!ligne.est_synthese_hybride && ligne.paiement?.date_paiement && (
                          <p className="whitespace-nowrap text-xs text-gray-500">{formatDate(ligne.paiement.date_paiement)}</p>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <PointagesManquantsCell ligne={ligne} />
                    </td>

                    <td className="px-4 py-4 align-top">
                      {ligne.est_synthese_hybride && !ligne.alertes?.length ? (
                        <span className="text-xs text-gray-500">Synthèse de lecture, non additionnée au total général</span>
                      ) : ligne.alertes?.length ? (
                        <div className="space-y-1">
                          {ligne.alertes.map((alerte, alerteIndex) => (
                            <div key={alerteIndex} className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800 flex items-start gap-1.5">
                              <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                              <span>{alerte}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="inline-flex h-6 items-center whitespace-nowrap rounded bg-green-100 px-2 text-xs font-medium text-green-800">
                          <CheckCircle size={12} className="mr-1" />
                          Prêt pour dotation
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <TablePagination
            pageIndex={safePageIndex}
            pageCount={pageCount}
            totalRows={totalRows}
            firstIndex={firstIndex}
            lastIndex={lastIndex}
            onPrevious={() => setPageIndex((current) => Math.max(0, current - 1))}
            onNext={() => setPageIndex((current) => Math.min(pageCount - 1, current + 1))}
          />
          </>
        )}
      </div>
    </div>
  );
};

export default SalaryTableSection;
